import axios from 'axios';

// Local HTTPS dev: the event payment page must be served over https://localhost:3002
// for CyberSource Unified Checkout, so route API calls through CRA's dev proxy (relative
// path) to avoid mixed-content. Production / http dev keeps the configured absolute URL.
const API_URL =
  process.env.NODE_ENV === 'development' &&
  typeof window !== 'undefined' &&
  window.location.protocol === 'https:'
    ? '/api/v1'
    : process.env.REACT_APP_API_URL || 'http://localhost:3000';

/**
 * Get event ticket payment information via token
 * @param {string} token - The unique payment token from the event order
 * @returns {Promise<Object>} Payment info including event details, tickets, and payment progress
 */
export const getEventTicketPaymentInfo = async (token) => {
  const response = await axios.get(`${API_URL}/events/ticket-payment/${token}`);
  return response.data;
};

/**
 * Initiate payment for selected event tickets and/or upgrade charges
 * @param {string} token - The unique payment token
 * @param {Object} data - Payment data
 * @param {number[]} [data.ticketIds] - Array of ticket (order item) IDs to pay for
 * @param {number[]} [data.upgradeChargeItemIds] - Array of item IDs whose upgrade charge to settle
 * @param {string} [data.payerName] - Optional payer name
 * @param {string} [data.payerEmail] - Optional payer email
 * @returns {Promise<Object>} Payment response with Paymob details and iframe URL
 */
export const initiateEventTicketPayment = async (token, data) => {
  const response = await axios.post(`${API_URL}/events/ticket-payment/${token}/pay`, data);
  return response.data;
};

/**
 * Confirm a Bank Misr (CyberSource Unified Checkout) event payment.
 * @param {string} token - The unique payment token
 * @param {Object} data - { orderCode, transientToken, payerName?, payerEmail?, currency? }
 * @returns {Promise<{success: boolean, status: string, message?: string}>}
 */
export const confirmEventTicketPayment = async (token, data) => {
  const response = await axios.post(`${API_URL}/events/ticket-payment/${token}/confirm`, data);
  return response.data;
};

/**
 * Format amount from cents to display value
 * @param {number} cents - Amount in cents
 * @returns {string} Formatted amount (e.g., "100.00")
 */
export const formatAmount = (cents) => {
  return (cents / 100).toFixed(2);
};
